import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Header from './layouts/Header';
import Footer from './layouts/Footer';
import { loadProducts } from './utils/loadProducts';

// Lazy load page components for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutUsPage = lazy(() => import('./pages/AboutUs'));
const ProductsPage = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const RequestQuote = lazy(() => import('./pages/RequestQuote'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-customIndigo"></div>
  </div>
);

function App() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      const data = await loadProducts();
      setProducts(data);
    };

    fetchProducts();
  }, []);

  return (
    <HelmetProvider>
        <Router>
          <div>
            <Header />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <>
                      <Helmet>
                        <title>Trusted Frozen Food Provider | PT Central Prima Sukses</title>
                        <meta name="description" content="PT Central Prima Sukses is Batam's leading frozen food distributor. Experience the finest selection of frozen seafood, chicken nuggets, frankfurters, and more. Quality you can trust, convenience you'll love since 1994." />
                        <link rel="canonical" href="https://www.centralprimasukses.co.id/" />

                        {/* Open Graph tags */}
                        <meta property="og:title" content="Trusted Frozen Food Provider | PT Central Prima Sukses" />
                        <meta property="og:description" content="PT Central Prima Sukses is Batam's leading frozen food distributor. Experience the finest selection of frozen seafood, chicken nuggets, frankfurters, and more." />
                        <meta property="og:type" content="website" />
                        <meta property="og:url" content="https://www.centralprimasukses.co.id/" />
                        <meta property="og:image" content="https://res.cloudinary.com/daiz8ag0l/image/upload/v1749396535/fresh_spwkew.webp" />

                        {/* Twitter Card tags */}
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:title" content="Trusted Frozen Food Provider | PT Central Prima Sukses" />
                        <meta name="twitter:description" content="PT Central Prima Sukses is Batam's leading frozen food distributor. Experience the finest selection of frozen seafood, chicken nuggets, frankfurters, and more." />
                        <meta name="twitter:image" content="https://res.cloudinary.com/daiz8ag0l/image/upload/v1749396535/fresh_spwkew.webp" />
                      </Helmet>
                      <HomePage />
                    </>
                  }
                />
                {/* 👇 Redirect /home to / */}
              <Route path="/home" element={<Navigate to="/" replace />} />
                <Route
                  path="/about"
                  element={
                    <>
                      <Helmet>
                        <title>About PT Central Prima Sukses - Leading Frozen Food Distributor in Batam</title>
                        <meta name="description" content="Learn about PT Central Prima Sukses, founded in 2012 with roots since 1994. We're Batam's trusted frozen food distributor offering premium quality products, flexible orders, and fast delivery. Discover our vision, mission, and values." />
                        <link rel="canonical" href="https://www.centralprimasukses.co.id/about" />

                        {/* Open Graph tags */}
                        <meta property="og:title" content="About PT Central Prima Sukses - Leading Frozen Food Distributor" />
                        <meta property="og:description" content="Learn about PT Central Prima Sukses, founded in 2012 with roots since 1994. We're Batam's trusted frozen food distributor offering premium quality products." />
                        <meta property="og:type" content="website" />
                        <meta property="og:url" content="https://www.centralprimasukses.co.id/about" />
                        <meta property="og:image" content="https://res.cloudinary.com/daiz8ag0l/image/upload/v1751953515/food-assortment_chd65v.jpg" />

                        {/* Twitter Card tags */}
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:title" content="About PT Central Prima Sukses - Leading Frozen Food Distributor" />
                        <meta name="twitter:description" content="Learn about PT Central Prima Sukses, founded in 2012 with roots since 1994. We're Batam's trusted frozen food distributor." />
                        <meta name="twitter:image" content="https://res.cloudinary.com/daiz8ag0l/image/upload/v1751953515/food-assortment_chd65v.jpg" />
                      </Helmet>
                      <AboutUsPage />
                    </>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <>
                      <Helmet>
                        <title>Frozen Food Products - Fish Balls, Nuggets, Sausages & More | CPS</title>
                        <meta name="description" content="Browse our extensive catalog of premium frozen foods including fish balls, chicken nuggets, frankfurters, spring rolls, pratha, steam buns, and more. Quality frozen products from trusted brands delivered fresh to Batam." />
                        <link rel="canonical" href="https://www.centralprimasukses.co.id/products" />

                        {/* Open Graph tags */}
                        <meta property="og:title" content="Frozen Food Products - Fish Balls, Nuggets, Sausages & More" />
                        <meta property="og:description" content="Browse our extensive catalog of premium frozen foods including fish balls, chicken nuggets, frankfurters, spring rolls, and more from trusted brands." />
                        <meta property="og:type" content="website" />
                        <meta property="og:url" content="https://www.centralprimasukses.co.id/products" />
                        <meta property="og:image" content="https://res.cloudinary.com/daiz8ag0l/image/upload/v1751953742/food-bg_hqpvpn.png" />

                        {/* Twitter Card tags */}
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:title" content="Frozen Food Products - Fish Balls, Nuggets, Sausages & More" />
                        <meta name="twitter:description" content="Browse our extensive catalog of premium frozen foods from trusted brands." />
                        <meta name="twitter:image" content="https://res.cloudinary.com/daiz8ag0l/image/upload/v1751953742/food-bg_hqpvpn.png" />
                      </Helmet>
                      <ProductsPage products={products} />
                    </>
                  }
                />
                <Route
                  path="/products/:slug"
                  element={
                    <>
                      <ProductDetail products={products} />
                    </>
                  }
                />
                <Route
                  path="/contact-us"
                  element={
                    <>
                      <Helmet>
                        <title>Contact PT Central Prima Sukses - Get in Touch Today</title>
                        <meta name="description" content="Contact PT Central Prima Sukses for inquiries about our frozen food products. Located in Batam, Indonesia. Reach out via phone, email, or visit our office. We're here to serve you with quality and convenience." />
                        <link rel="canonical" href="https://www.centralprimasukses.co.id/contact-us" />

                        {/* Open Graph tags */}
                        <meta property="og:title" content="Contact PT Central Prima Sukses - Get in Touch Today" />
                        <meta property="og:description" content="Contact PT Central Prima Sukses for inquiries about our frozen food products. Located in Batam, Indonesia." />
                        <meta property="og:type" content="website" />
                        <meta property="og:url" content="https://www.centralprimasukses.co.id/contact-us" />
                        <meta property="og:image" content="https://res.cloudinary.com/daiz8ag0l/image/upload/v1749396535/fresh_spwkew.webp" />

                        {/* Twitter Card tags */}
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:title" content="Contact PT Central Prima Sukses - Get in Touch Today" />
                        <meta name="twitter:description" content="Contact PT Central Prima Sukses for inquiries about our frozen food products. Located in Batam, Indonesia." />
                        <meta name="twitter:image" content="https://res.cloudinary.com/daiz8ag0l/image/upload/v1749396535/fresh_spwkew.webp" />
                      </Helmet>
                      <ContactUs />
                    </>
                  }
                />
                <Route
                  path="/request-quote"
                  element={
                    <>
                      <Helmet>
                        <title>Request a Quote - Bulk Frozen Food Orders | PT Central Prima Sukses</title>
                        <meta name="description" content="Request a quote for bulk frozen food orders from PT Central Prima Sukses. Flexible order quantities for businesses and retailers in Batam. Fast delivery, competitive pricing, and premium quality guaranteed." />
                        <link rel="canonical" href="https://www.centralprimasukses.co.id/request-quote" />

                        {/* Open Graph tags */}
                        <meta property="og:title" content="Request a Quote - Bulk Frozen Food Orders" />
                        <meta property="og:description" content="Request a quote for bulk frozen food orders from PT Central Prima Sukses. Flexible quantities, fast delivery, competitive pricing." />
                        <meta property="og:type" content="website" />
                        <meta property="og:url" content="https://www.centralprimasukses.co.id/request-quote" />
                        <meta property="og:image" content="https://res.cloudinary.com/daiz8ag0l/image/upload/v1749396535/fresh_spwkew.webp" />

                        {/* Twitter Card tags */}
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:title" content="Request a Quote - Bulk Frozen Food Orders" />
                        <meta name="twitter:description" content="Request a quote for bulk frozen food orders from PT Central Prima Sukses. Flexible quantities, fast delivery." />
                        <meta name="twitter:image" content="https://res.cloudinary.com/daiz8ag0l/image/upload/v1749396535/fresh_spwkew.webp" />
                      </Helmet>
                      <RequestQuote />
                    </>
                  }
                />
                {/* Add other routes here */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <Footer />
          </div>
        </Router>
    </HelmetProvider>
  );
}

export default App;
